import { ThemeEvents } from '@theme/events';

/*
 * MOKY (09.10.2026):
 * - Add-ons (the return warranty, handle "30-day-free-returns") do not count
 *   towards free shipping.
 * - With the Premium warranty in the cart the free-shipping goal is CHF 40
 *   instead of CHF 50, and a small navy "Premium -10 CHF" chip shows to the
 *   left of the message (only while the Premium variant is in the cart).
 *   Set PREMIUM_ENABLED to false to switch this off.
 * - 29.10.2026: the premium custom packaging (handle "premium-custom-packaging")
 *   does not count towards free shipping either.
 */
const ADDON_HANDLE = '30-day-free-returns';
/** Premium return warranty is its own product since 10.10.2026. */
const PREMIUM_HANDLE = 'return-warranty-premium';
const PACKAGING_HANDLE = 'premium-custom-packaging';
const isAddon = (item) =>
  item.handle === ADDON_HANDLE || item.handle === PREMIUM_HANDLE || item.handle === PACKAGING_HANDLE;
const PREMIUM_ENABLED = true;
const PREMIUM_THRESHOLD = 4000;

/**
 * @returns {number}
 */
function presentmentRate() {
  const rate = Number.parseFloat(globalThis.Shopify?.currency?.rate ?? '1');
  return Number.isFinite(rate) && rate > 0 ? rate : 1;
}

/**
 * Round cents up to a whole currency unit (never down).
 * @param {number} cents
 * @returns {number}
 */
function ceilToCurrencyUnit(cents) {
  const amount = Number(cents);
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  return Math.ceil(amount / 100) * 100;
}

class FreeShippingBar extends HTMLElement {
  #currentProgress = 0;
  #fillAnim = null;
  #shopThreshold = 0;
  #baseThreshold = 0;
  #shopGiftThreshold = 0;
  #premium = false;

  connectedCallback() {
    this.messageEl = this.querySelector('[data-shipping-message]');
    this.fillEl = this.querySelector('[data-shipping-fill]');
    this.truckEl = this.querySelector('[data-shipping-truck]');
    this.giftEl = this.querySelector('[data-shipping-gift]');
    this.#baseThreshold = Number(this.dataset.thresholdShop) || Number(this.dataset.threshold) || 0;
    this.#shopThreshold = this.#baseThreshold;
    this.#shopGiftThreshold = Number(this.dataset.giftThresholdShop) || Number(this.dataset.giftThreshold) || this.#shopThreshold;
    this.messageAway = this.dataset.messageAway || '';
    this.messageUnlocked = this.dataset.messageUnlocked || '';
    this.messageGift = this.dataset.messageGift || '';
    this.#ensureBadge();
    this.#applyPresentment();
    this.#currentProgress = this.#readCurrentProgress();

    const initialSubtotal = Number(this.dataset.cartAmount);
    if (Number.isFinite(initialSubtotal)) {
      this.#render(initialSubtotal);
    }

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.#onCartUpdate);

    // Re-read the real cart once so add-ons and Premium are taken into account.
    this.#onCartUpdate(new Event('moky:init'));
  }

  disconnectedCallback() {
    this.#fillAnim?.cancel();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.#onCartUpdate);
  }

  #ensureBadge() {
    if (!this.messageEl || this.querySelector('.moky-fsb-badge')) return;
    const saved = Math.round((this.#baseThreshold - PREMIUM_THRESHOLD) / 100);
    const head = document.createElement('div');
    head.className = 'moky-fsb-head';
    this.messageEl.insertAdjacentElement('beforebegin', head);
    head.appendChild(this.messageEl);
    const badge = document.createElement('span');
    badge.className = 'moky-fsb-badge';
    badge.hidden = true;
    badge.innerHTML = `<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 1.75 2.75 3.9v3.4c0 2.98 2.1 5.42 5.25 6.95 3.15-1.53 5.25-3.97 5.25-6.95V3.9L8 1.75Z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="m5.9 7.9 1.5 1.5 2.9-3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg><span class="moky-fsb-badge__name">Premium</span><span class="moky-fsb-badge__save">-${saved} CHF</span>`;
    // Before the message (left side), so it never sits over the truck icon,
    // which always rests at the 75% mark of the bar below.
    head.insertBefore(badge, this.messageEl);
    this.badgeEl = badge;
  }

  #applyPresentment() {
    const rate = presentmentRate();
    this.threshold = ceilToCurrencyUnit(this.#shopThreshold * rate);
    this.giftThreshold = Math.max(ceilToCurrencyUnit(this.#shopGiftThreshold * rate), this.threshold);
    this.currency = globalThis.Shopify?.currency?.active || this.dataset.currency || '';
    this.dataset.threshold = String(this.threshold);
    this.dataset.giftThreshold = String(this.giftThreshold);
    this.dataset.currency = this.currency;
  }

  /**
   * @param {Event} event
   */
  #onCartUpdate = async (event) => {
    const resource = /** @type {{ detail?: { resource?: Record<string, unknown> } }} */ (event).detail?.resource;
    let cart = resource && typeof resource === 'object' && Array.isArray(resource.items) ? resource : null;

    if (!cart) {
      try {
        cart = await fetch('/cart.js').then((response) => response.json());
      } catch (error) {
        console.error(error);
        return;
      }
    }

    this.#readPremium(cart);
    this.#applyPresentment();
    this.#render(this.#readSubtotal(cart) || 0);
  };

  /**
   * @param {Record<string, any>} cart
   */
  #readPremium(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    this.#premium =
      PREMIUM_ENABLED &&
      (globalThis.Shopify?.currency?.active || 'CHF') === 'CHF' &&
      items.some((item) => item.handle === PREMIUM_HANDLE || (item.handle === ADDON_HANDLE && /premium/i.test(item.variant_title || '')));
    this.#shopThreshold = this.#premium ? PREMIUM_THRESHOLD : this.#baseThreshold;
    this.toggleAttribute('data-premium', this.#premium);
    if (this.badgeEl) this.badgeEl.hidden = !this.#premium;
  }

  /**
   * @param {Record<string, any> | undefined} cart
   * @returns {number | null}
   */
  #readSubtotal(cart) {
    if (!cart || typeof cart !== 'object') return null;

    const value = cart.items_subtotal_price ?? cart.original_total_price ?? cart.total_price;
    let amount = Number(value);
    if (!Number.isFinite(amount)) return null;

    // Add-ons (return warranty, premium packaging) do not count towards free shipping
    if (Array.isArray(cart.items)) {
      for (const item of cart.items) {
        if (isAddon(item)) amount -= Number(item.final_line_price ?? item.line_price ?? 0);
      }
    }

    return Math.max(0, amount);
  }

  #readCurrentProgress() {
    if (!this.fillEl) return 0;

    const value = Number.parseFloat(this.fillEl.style.width);
    return Number.isFinite(value) ? value : 0;
  }

  /**
   * Map cart amount onto the bar: 0–shipping → 0–75%, shipping–gift → 75–100%.
   *
   * @param {number} subtotal
   */
  #progressFor(subtotal) {
    const shipping = this.threshold;
    const gift = Math.max(this.giftThreshold, shipping);

    if (shipping <= 0) return 0;
    if (subtotal <= 0) return 0;
    if (subtotal >= gift) return 100;
    if (subtotal <= shipping) return (subtotal / shipping) * 75;

    return 75 + ((subtotal - shipping) / (gift - shipping)) * 25;
  }

  #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * @param {number} progress
   */
  #animateFill(progress) {
    if (!this.fillEl) return;

    const from = this.#currentProgress;
    const to = progress;
    const delta = Math.abs(to - from);

    this.#fillAnim?.cancel();
    this.classList.toggle('is-dropping', to < from);

    if (delta < 0.15 || this.#prefersReducedMotion()) {
      this.classList.remove('is-moving');
      this.fillEl.style.width = `${to}%`;
      this.#currentProgress = to;
      return;
    }

    this.fillEl.style.width = `${from}%`;
    this.classList.add('is-moving');

    const duration = Math.min(3600, Math.max(2400, 1800 + delta * 28));

    this.#fillAnim = this.fillEl.animate(
      [{ width: `${from}%` }, { width: `${to}%` }],
      {
        duration,
        easing: to >= from ? 'cubic-bezier(0.16, 0.84, 0.32, 1)' : 'cubic-bezier(0.33, 1, 0.68, 1)',
        fill: 'forwards',
      }
    );

    this.#fillAnim.finished
      .then(() => {
        this.fillEl.style.width = `${to}%`;
        this.#fillAnim?.commitStyles();
        this.#fillAnim?.cancel();
        this.#fillAnim = null;
        this.classList.remove('is-moving');
      })
      .catch(() => {
        this.#fillAnim = null;
      });

    this.#currentProgress = to;
  }

  /**
   * @param {number} subtotal
   */
  #render(subtotal) {
    if (!this.messageEl || !this.fillEl || this.threshold <= 0) return;

    const remainingShipping = Math.max(0, this.threshold - subtotal);
    const remainingGift = Math.max(0, this.giftThreshold - subtotal);
    const progress = this.#progressFor(subtotal);

    this.#animateFill(progress);

    this.toggleAttribute('data-unlocked', remainingShipping <= 0);
    this.toggleAttribute('data-gift-unlocked', remainingGift <= 0);
    this.truckEl?.toggleAttribute('data-reached', remainingShipping <= 0);
    this.giftEl?.toggleAttribute('data-reached', remainingGift <= 0);

    if (remainingGift <= 0) {
      this.messageEl.textContent = this.messageGift;
      return;
    }

    if (remainingShipping <= 0) {
      this.messageEl.textContent = this.messageUnlocked;
      return;
    }

    const amount = `${Math.ceil(remainingShipping / 100)} ${this.currency}`.trim();
    this.messageEl.textContent = this.messageAway.replaceAll('[amount]', amount);
  }
}

if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBar);
}
